import { ColumnState } from "ag-grid-community";
import { DateRange } from "react-day-picker";
import { create } from "zustand";

import { getUserSearchFilterDefaults } from "src/hooks/useInitUserSearchFilter";

import {
  defaultKSK2130_R01DetailDto,
  defaultKSK2130_R01HeaderDto,
  KSK2130_R01DetailDto,
  KSK2130_R01HeaderDto,
} from "./Dto_KSK2130_R01";

export interface KSK2130_R01Filter {
  compCd: string;
  dateRange: DateRange | undefined;
  workplaceCd: string;
  orderEmp: string;
  orderEmpNm: string;
  barcodeInput: string;
}

export interface Store_KSK2130_R01 {
  KSK2130_R01Filter: KSK2130_R01Filter;
  setKSK2130_R01Filter: (filter: Partial<KSK2130_R01Filter>) => void;

  KSK2130_R01List: KSK2130_R01HeaderDto[];
  setKSK2130_R01List: (list: KSK2130_R01HeaderDto[]) => void;
  selectedKSK2130_R01: KSK2130_R01HeaderDto | undefined;
  setSelectedKSK2130_R01: (data: KSK2130_R01HeaderDto | undefined) => void;
  initSelectedKSK2130_R01: () => void;

  KSK2130_R01DetailList: KSK2130_R01DetailDto[];
  setKSK2130_R01DetailList: (list: KSK2130_R01DetailDto[]) => void;
  insertKSK2130_R01DetailList: (list: KSK2130_R01DetailDto[]) => void;
  removeKSK2130_R01DetailList: (valueIndexList: string[]) => void;

  selectedKSK2130_R01DetailList: string[];
  addSelectedKSK2130_R01DetailList: (valueIndexList: string[]) => void;
  removeSelectedKSK2130_R01DetailList: (valueIndexList: string[]) => void;
  initSelectedKSK2130_R01DetailList: () => void;

  deletedKSK2130_R01DetailList: string[];
  addDeletedKSK2130_R01DetailList: (valueIndexList: string[]) => void;
  removeDeletedKSK2130_R01DetailList: (valueIndexList?: string[]) => void;
  originalKSK2130_R01DetailList: KSK2130_R01DetailDto[];

  isMaterialLotModalVisible: boolean;
  setIsMaterialLotModalVisible: (isVisible: boolean) => void;
  KSK2130_R01ModalList: KSK2130_R01DetailDto[];
  setKSK2130_R01ModalList: (list: KSK2130_R01DetailDto[]) => void;

  columnState: ColumnState[] | null;
  setColumnState: (state: ColumnState[] | null) => void;
  scrollTop: number | null;
  setScrollTop: (top: number | null) => void;
  detailColumnState: ColumnState[] | null;
  setDetailColumnState: (state: ColumnState[] | null) => void;
  detailScrollTop: number | null;
  setDetailScrollTop: (top: number | null) => void;

  initData: () => void;
}

const createDefaultKSK2130_R01Filter = (): KSK2130_R01Filter => ({
  ...getUserSearchFilterDefaults(),
  dateRange: {
    from: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
    to: new Date(),
  },
  orderEmp: "",
  orderEmpNm: "",
  barcodeInput: "",
});

export const defaultKSK2130_R01Filter = createDefaultKSK2130_R01Filter();

const getKSK2130_R01DetailKey = (row: KSK2130_R01DetailDto) =>
  `${row.itemCd}|${row.lotNo}`;

export const useKSK2130_R01Store = create<Store_KSK2130_R01>((set) => ({
  KSK2130_R01Filter: defaultKSK2130_R01Filter,
  setKSK2130_R01Filter: (filter) =>
    set((state) => ({
      KSK2130_R01Filter: { ...state.KSK2130_R01Filter, ...filter },
    })),

  KSK2130_R01List: [],
  setKSK2130_R01List: (list) =>
    set({
      KSK2130_R01List: list.map((item, index) => ({
        ...defaultKSK2130_R01HeaderDto,
        ...item,
        valueIndex: index + 1,
        orderIndex: index + 1,
        orderIndexSeq: 0,
      })),
    }),
  selectedKSK2130_R01: undefined,
  setSelectedKSK2130_R01: (data) => set({ selectedKSK2130_R01: data }),
  initSelectedKSK2130_R01: () => set({ selectedKSK2130_R01: undefined }),

  KSK2130_R01DetailList: [],
  setKSK2130_R01DetailList: (list) => {
    const detailList = list.map((item, index) => ({
      ...defaultKSK2130_R01DetailDto,
      ...item,
      valueIndex: index + 1,
      orderIndex: index + 1,
      orderIndexSeq: 0,
      inserted: false,
    }));
    set({
      KSK2130_R01DetailList: detailList,
      originalKSK2130_R01DetailList: detailList,
      selectedKSK2130_R01DetailList: [],
      deletedKSK2130_R01DetailList: [],
    });
  },
  insertKSK2130_R01DetailList: (list) =>
    set((state) => {
      const existingKeySet = new Set(
        state.KSK2130_R01DetailList.map(getKSK2130_R01DetailKey),
      );
      const detailList = [...state.KSK2130_R01DetailList];
      let nextValueIndex =
        Math.max(
          0,
          ...state.KSK2130_R01DetailList.map((item) => item.valueIndex),
        ) + 1;

      list.forEach((item) => {
        const key = getKSK2130_R01DetailKey(item);
        if (existingKeySet.has(key)) return;

        detailList.push({
          ...defaultKSK2130_R01DetailDto,
          ...item,
          valueIndex: nextValueIndex,
          orderIndex: nextValueIndex,
          orderIndexSeq: 0,
          lotSeq: 0,
          inserted: true,
        });
        nextValueIndex++;
        existingKeySet.add(key);
      });

      return { KSK2130_R01DetailList: detailList };
    }),
  removeKSK2130_R01DetailList: (valueIndexList) =>
    set((state) => ({
      KSK2130_R01DetailList: state.KSK2130_R01DetailList.filter(
        (item) =>
          !valueIndexList.some(
            (valueIndex) => Number(valueIndex) === item.valueIndex,
          ),
      ),
    })),

  selectedKSK2130_R01DetailList: [],
  addSelectedKSK2130_R01DetailList: (valueIndexList) =>
    set((state) => ({
      selectedKSK2130_R01DetailList: Array.from(
        new Set([...state.selectedKSK2130_R01DetailList, ...valueIndexList]),
      ),
    })),
  removeSelectedKSK2130_R01DetailList: (valueIndexList) =>
    set((state) => ({
      selectedKSK2130_R01DetailList:
        state.selectedKSK2130_R01DetailList.filter(
          (valueIndex) => !valueIndexList.includes(valueIndex),
        ),
    })),
  initSelectedKSK2130_R01DetailList: () =>
    set({ selectedKSK2130_R01DetailList: [] }),

  deletedKSK2130_R01DetailList: [],
  addDeletedKSK2130_R01DetailList: (valueIndexList) =>
    set((state) => ({
      deletedKSK2130_R01DetailList: Array.from(
        new Set([
          ...state.deletedKSK2130_R01DetailList,
          ...valueIndexList.filter((valueIndex) =>
            state.KSK2130_R01DetailList.some(
              (item) =>
                item.valueIndex === Number(valueIndex) && !item.inserted,
            ),
          ),
        ]),
      ),
    })),
  removeDeletedKSK2130_R01DetailList: (valueIndexList) =>
    set((state) => ({
      deletedKSK2130_R01DetailList: valueIndexList
        ? state.deletedKSK2130_R01DetailList.filter(
            (valueIndex) => !valueIndexList.includes(valueIndex),
          )
        : [],
    })),
  originalKSK2130_R01DetailList: [],

  isMaterialLotModalVisible: false,
  setIsMaterialLotModalVisible: (isVisible) =>
    set({ isMaterialLotModalVisible: isVisible }),
  KSK2130_R01ModalList: [],
  setKSK2130_R01ModalList: (list) =>
    set({
      KSK2130_R01ModalList: list.map((item, index) => ({
        ...defaultKSK2130_R01DetailDto,
        ...item,
        valueIndex: index + 1,
        orderIndex: index + 1,
        orderIndexSeq: 0,
        inserted: false,
      })),
    }),

  columnState: null,
  setColumnState: (state) => set({ columnState: state }),
  scrollTop: null,
  setScrollTop: (top) => set({ scrollTop: top }),
  detailColumnState: null,
  setDetailColumnState: (state) => set({ detailColumnState: state }),
  detailScrollTop: null,
  setDetailScrollTop: (top) => set({ detailScrollTop: top }),

  initData: () =>
    set({
      KSK2130_R01Filter: createDefaultKSK2130_R01Filter(),
      KSK2130_R01List: [],
      selectedKSK2130_R01: undefined,
      KSK2130_R01DetailList: [],
      selectedKSK2130_R01DetailList: [],
      deletedKSK2130_R01DetailList: [],
      originalKSK2130_R01DetailList: [],
      isMaterialLotModalVisible: false,
      KSK2130_R01ModalList: [],
      columnState: null,
      scrollTop: null,
      detailColumnState: null,
      detailScrollTop: null,
    }),
}));
